import { useReducer } from "react";
import { useQuery } from "@apollo/client";
import { FILTER_USERS_QUERY } from "../graphql/query";
import { FilterUsers, FilterUsersVariables } from "../graphql/__generated_types__/FilterUsers";

type IdAndDisplayName = { id: number; displayName: string };

const Action = {
  DisplayNameFilterChanged: "DISPLAY_NAME_FILTER_CHANGED",
  DataRecieved: "DATA_RECEIVED",
};

const deDuplicateById = (arr: { id: number }[]) =>
  arr.filter((value, index) => arr.findIndex(({ id }) => id === value.id) === index);

function reducer(state: any, action: { type: string; value?: any }) {
  switch (action.type) {
    case Action.DisplayNameFilterChanged:
      return {
        ...state,
        userCount: null,
        users: [...(action.value.requiredUsers || [])],
        displayNameFilter: action.value.searchTerm,
        nextPage: 0,
      };
    case Action.DataRecieved:
      return {
        ...state,
        userCount: action.value.count,
        users: deDuplicateById([...state.users, ...action.value.users]),
        nextPage: state.nextPage + 1,
      };
    default:
      throw new Error(`Unknown action, ${action.type}`);
  }
}

const PAGE_SIZE = 15;

export const usePaginatedUserQuery = (args?: {
  requiredUsers?: IdAndDisplayName[];
  includeLoggedInUserFirst?: boolean;
}) => {
  const initialState = {
    displayNameFilter: undefined,
    userCount: null,
    users: [...(args?.requiredUsers || [])],
    nextPage: 0,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const filter = {
    displayName: state.displayNameFilter,
  };
  const { fetchMore, loading } = useQuery<FilterUsers, FilterUsersVariables>(FILTER_USERS_QUERY, {
    variables: {
      filter,
      offset: 0,
      limit: PAGE_SIZE + (args?.includeLoggedInUserFirst ? 1 : 0),
      includeLoggedInUserFirst: args?.includeLoggedInUserFirst,
    },
    onCompleted: (data) => {
      dispatch({ type: Action.DataRecieved, value: data.filterUsers });
    },
  });
  const hasMore = state.users.length < state.userCount;
  const loadMore = async () => {
    if (hasMore) {
      const { data } = await fetchMore({ variables: { offset: state.nextPage * PAGE_SIZE } });
      dispatch({ type: Action.DataRecieved, value: data.filterUsers });
    }
  };
  const search = (value: string, requiredUsers?: IdAndDisplayName[]) =>
    dispatch({
      type: Action.DisplayNameFilterChanged,
      value: { searchTerm: value, requiredUsers },
    });

  return { state, search, loadMore, loading };
};
